import {FC, useMemo} from 'react';

import {Box} from '@mui/material';
import {t} from 'i18next';

import {MLogo} from 'assets/icons/mLogo';
import theme from 'config/theme';
import {
  isBasketDiversifyFlagEnabled,
  isDisabledButAllowedToOrderForAS,
  isPDPRevamp,
  isShowAddtoCartBtn,
  removeImageSkeleton,
  shopping1PNextDayEnabled,
  showOnlyAddToCartButton
} from 'constants/recommendation';
import {BASKET_PDP_ATTRIBUTES} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useCurrentBasketItem} from 'hooks/useCurrentBasketItem';
import {useProductAvailability} from 'hooks/useProductAvailability';
import {useProductPriceAndText} from 'hooks/useProductPriceAndText';
import {useRibbons} from 'hooks/useRibbons';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {JSONObject, Product, ProductType} from 'types';
import {appsFlyerMarketingAnalytics, marketingAnalytics, productAnalytics, ProductAvailability} from 'utils';
import {getProPointPromotions} from 'utils/product/getProPointPromotions';
import {ProductModel} from 'utils/product/types';
import {getBasketRecommendationAnalyticTitle} from 'utils/recommendation-utils/recommendations';

import {
  LeoBrand,
  LeoBrandContainer,
  LeoContentContainer,
  LeoDelivery,
  LeoImageContainer,
  LeoImageWrapper,
  LeoMarketingRibbonContainer,
  LeoMiddleWrapper,
  LeoPackInfo,
  LeoProductName,
  LeoProductNameWrapper,
  LeoPromotionBadge,
  ProductCardLEOWrapper
} from '../carousel-product-card-list/styles';
import ProPointPromotionBox from '../pro-point-promotion-box';
import {WeightedFreshPriceComponent} from '../weighted-fresh-price';
import {LeoImageSkeletonWrapper, ProductCardLEOContainer} from './styles';
import ImageLoader, {ImageLoaderProps} from '../image-loader';
import {LeoDeliveryInfo} from '../leo-delivery-info';
import {LeoPromotionsBadge} from '../leo-promotions-badge';
import {PLPDeliveryInfo} from '../plp_delivery_Info';
import ProductCardLEOdiscountOrSlabPriceBox from '../product-card-leodiscount-or-slab-price-box';
import {RightRibbon} from '../right-ribbon';
import {ProductCardProps} from './types';
import ProductCardLEOPriceBox from '../product-card-leo-price-box';
import ProductStatus from '../product-status';

export const ProductCard: FC<ProductCardProps> = ({
  sendCarouselImpressionAnalytics,
  product,
  isSearchBoxCarousel,
  carouselIndex,
  itemIndex
}) => {
  const {recommendations} = useAppSelector((state) => state.recommendations);
  const {isExpress} = useAppMode();
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const {basketItem} = useCurrentBasketItem(product);
  const isProductServiceDataReady = product?.variants?.length > 0;
  const quantity = basketItem?.quantity || 0;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const images = urlRegex.test(product.imageUrls[0]);
  const {promotionInfoWithUrl, productType, rightRibbonInfo, isBulky, isPreOrder} = useRibbons(product);
  const is1PBulky = shopping1PNextDayEnabled && !isPDPRevamp && isBulky && productType === ProductType.PRODUCT_1P;

  const {
    isDisabled,
    status: productStatus,
    isOutOfStock,
    isNotDeliverable: isOutsideZone
  } = useProductAvailability(product);

  const {isSlabPrice, isDiscounted} = useProductPriceAndText({product, productCount: quantity});

  const isProductOutOfStockorOutsideZone = isOutOfStock || isOutsideZone;
  const isProductNotEligible = productStatus === ProductAvailability.notEligible;

  const isAddToCart = true;
  const addAnalytics = (productId?: string, analyticsProps?: any) => {
    sendCarouselImpressionAnalytics && sendCarouselImpressionAnalytics();
    productId && SendBrowserEvent.goBasketRecommendationToPDP(product.id, analyticsProps);
  };

  const getImageSkeletonMLoader = () => (
    <LeoImageSkeletonWrapper>
      <MLogo width={48} height={48} />
    </LeoImageSkeletonWrapper>
  );

  const imageProps: ImageLoaderProps = {
    image: {
      src: images ? product.imageUrls[0] : '',
      alt: product.title,
      objectFit: 'contain',
      sizes: '25vw'
    },
    skelton: {
      width: 144,
      height: 144,
      variant: 'rounded',
      style: {backgroundColor: theme.palette.grey[100]},
      children: getImageSkeletonMLoader()
    }
  };
  const {promotionSuggestion, proPointPromotionsApplicable, totalPromotions} = useMemo(() => {
    return getProPointPromotions(product as unknown as ProductModel);
  }, [product]);

  const handleProductClick = (triggerType: string) => {
    !isReactNativeView && SendBrowserEvent.closeBasketDrawer();
    const analyticsProps = {
      carouselIndex,
      carouselName: `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`,
      algorithm: recommendations.algorithm,
      quantity,
      indexClicked: itemIndex,
      triggerType,
      position: product?.position,
      getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      ...(product?.recommendationScore !== undefined && {
        recommendationScore: product?.recommendationScore
      }),
      ...(product?.normalisedRecommendationScore !== undefined && {
        normalisedRecommendationScore: product?.normalisedRecommendationScore
      }),
      ...(product?.alreadyPurchased !== undefined && {
        hasAlreadyPurchased: product?.alreadyPurchased
      }),
      ...(productAnalytics(product) as JSONObject),
      ...(!isReactNativeView && marketingAnalytics(product as Product)),
      ...(isReactNativeView && appsFlyerMarketingAnalytics(product)),
      ...(product?.isSponsored !== undefined && {
        isSponsored: product?.isSponsored
      }),
      ...(isReactNativeView && {
        fromExpress: isExpress
      })
    };
    addAnalytics(product.id, analyticsProps);
  };

  const renderPriceComponent = () => {
    if (isDiscounted || isSlabPrice) {
      return (
        <ProductCardLEOdiscountOrSlabPriceBox
          product={product}
          isShowAddtoCartBtn={
            isShowAddtoCartBtn &&
            (!isProductOutOfStockorOutsideZone || isDisabledButAllowedToOrderForAS) &&
            !isProductNotEligible
          }
          handleClickAddToBasket={() =>
            handleProductClick(
              quantity > 0
                ? BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_QUANTITY_COUNT
                : BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_PLUS_BUTTON
            )
          }
          productCount={quantity}
          isAddToCart={isAddToCart}
          isPLP={true}
          addAnalytics={addAnalytics}
          sendCarouselImpressionAnalytics={sendCarouselImpressionAnalytics}
          showOnlyAddToCartButton={showOnlyAddToCartButton}
        />
      );
    } else {
      return (
        <ProductCardLEOPriceBox
          product={product}
          isShowAddtoCartBtn={
            isShowAddtoCartBtn &&
            (!isProductOutOfStockorOutsideZone || isDisabledButAllowedToOrderForAS) &&
            !isProductNotEligible
          }
          handleClickAddToBasket={() =>
            handleProductClick(
              quantity > 0
                ? BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_QUANTITY_COUNT
                : BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_PLUS_BUTTON
            )
          }
          productCount={quantity}
          isPDP={false}
          isAddToCart={isAddToCart}
          sendCarouselImpressionAnalytics={sendCarouselImpressionAnalytics}
          addAnalytics={addAnalytics}
          isSearchBoxCarousel={isSearchBoxCarousel}
          showOnlyAddToCartButton={showOnlyAddToCartButton}
        />
      );
    }
  };

  const productCardContent = (
    <ProductCardLEOWrapper>
      {isProductServiceDataReady && isDisabled && !isDisabledButAllowedToOrderForAS && (
        <ProductStatus status={t(`labels.${productStatus}`)} />
      )}
      <LeoImageContainer>
        <LeoImageWrapper>
          <ImageLoader className="product-card" removeImageSkeleton={removeImageSkeleton} {...imageProps} />
        </LeoImageWrapper>
      </LeoImageContainer>
      <LeoContentContainer>
        <LeoMiddleWrapper>
          {proPointPromotionsApplicable ? (
            <ProPointPromotionBox promotionSuggestion={promotionSuggestion} totalPromotions={totalPromotions} />
          ) : (
            <LeoPromotionBadge>
              <LeoPromotionsBadge
                className="promotion"
                removeImageSkeleton={removeImageSkeleton}
                promotionInfo={promotionInfoWithUrl}
              />
            </LeoPromotionBadge>
          )}
          <LeoProductNameWrapper>
            <LeoProductName>{product.title}</LeoProductName>
            <LeoPackInfo data-test-id={`${product.id}_unit`}>
              {product.isWeightedFresh ? (
                <WeightedFreshPriceComponent
                  className="plp"
                  displayPrice={product?.displayPrice}
                  priceUnit={product?.priceUnit}
                  size={product.size}
                  moq={product.moq}
                  isSellInPiece={product?.sellInPiece || false}
                />
              ) : (
                product.size
              )}
            </LeoPackInfo>
          </LeoProductNameWrapper>
          <LeoBrandContainer>
            <LeoBrand>{product?.brand}</LeoBrand>
            {isProductServiceDataReady && (
              <LeoDelivery>
                {isPDPRevamp ? (
                  <PLPDeliveryInfo product={product as Product}></PLPDeliveryInfo>
                ) : (
                  <LeoDeliveryInfo
                    productType={productType}
                    isBulky={isBulky}
                    shippingInfo={product?.shippingInfo}
                    isPreOrder={isPreOrder}
                    is1PBulky={is1PBulky}
                    is1PX={product.is1PX}
                  />
                )}
              </LeoDelivery>
            )}
          </LeoBrandContainer>
          <LeoMarketingRibbonContainer>
            {!isDisabled && (
              <RightRibbon
                rightRibbonInfo={rightRibbonInfo}
                isSlabPrice={isSlabPrice}
                className="plpLeo"
                iconSize={{width: 46, height: 32}}
                removeImageSkeleton={removeImageSkeleton}
              />
            )}
          </LeoMarketingRibbonContainer>
        </LeoMiddleWrapper>
        {renderPriceComponent()}
      </LeoContentContainer>
    </ProductCardLEOWrapper>
  );
  return (
    <ProductCardLEOContainer onClick={() => addAnalytics()}>
      <Box onClick={() => handleProductClick(BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_PRODUCT_CARD)}>
        {productCardContent}
      </Box>
    </ProductCardLEOContainer>
  );
};
